import type { OnChanges, OnInit, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  inject,
  Input,
} from '@angular/core';
import { Datastore } from '@freelancer/datastore';
import type {
  OnlineOfflineCollection,
  Thread,
  ThreadContext,
  ThreadsCollection,
} from '@freelancer/datastore/collections';
import {
  ThreadContextType,
  ThreadType,
} from '@freelancer/datastore/collections';
import { MessagingChat } from '@freelancer/messaging-chat';
import { ButtonColor, ButtonDisplay, ButtonSize } from '@freelancer/ui/button';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { OnlineIndicatorSize } from '@freelancer/ui/online-indicator';
import { FontWeight } from '@freelancer/ui/text';
import { UI_CONFIG } from '@freelancer/ui/ui.config';
import { UiConfig } from '@freelancer/ui/ui.interface';
import {
  UnreadIndicatorPosition,
  UnreadIndicatorSize,
} from '@freelancer/ui/unread-indicator';
import { ThreadTypeApi } from 'api-typings/messages/messages_types';
import { OnlineOfflineStatusApi } from 'api-typings/users/users';
import type { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'fl-chat-button',
  template: `
    <ng-template
      #button
      let-thread="thread"
    >
      <fl-button
        class="ChatButton"
        [attr.data-animate]="!!(unreadMessagesNumber$ | flAsync)"
        [attr.full-width]="fullWidth"
        [attr.icon-only]="isIconOnly"
        [color]="color"
        [disabled]="disabled"
        [displayDesktop]="displayDesktop"
        [displayTablet]="displayTablet"
        [display]="display"
        [size]="size"
        (click)="chat(thread?.id)"
      >
        <div
          class="ChatButton-wrapper"
          [attr.data-compact]="!showOnlineIndicator"
        >
          @if (!isIconOnly) {
            @if (showOnlineIndicator) {
              <fl-online-indicator
                class="ChatButton-content"
                [isOnline]="(isOnline$ | flAsync) === true"
                [size]="onlineIndicatorSize"
              ></fl-online-indicator>
            }
            <span
              class="text-color-inherit text-size-inherit ChatButton-content font-bold"
            >
              @if (uiConfig.theme === 'deloitte') {
                <ng-container i18n="Message button (verb)">
                  Message
                </ng-container>
              } @else {
                <ng-container>
                  {{ buttonText }}
                </ng-container>
              }
            </span>
            @if (unreadMessagesNumber$ | flAsync; as unReadNumber) {
              <fl-unread-indicator
                class="ChatButton-content"
                i18n-label="Unread messages indicator label"
                label="Unread messages"
                [counter]="unReadNumber"
                [position]="UnreadIndicatorPosition.INLINE"
              ></fl-unread-indicator>
            }
          } @else {
            <div class="ChatButton-iconContainer">
              <fl-icon
                class="ChatButton-icon"
                [color]="IconColor.INHERIT"
                [label]="iconLabel"
                [name]="'ui-chat-square'"
                [size]="IconSize.MID"
                [useIconFont]="true"
                [weight]="FontWeight.LIGHT"
              ></fl-icon>
              <div class="ChatButton-unreadIndicator">
                @if (unreadMessagesNumber$ | flAsync; as unReadNumber) {
                  <fl-unread-indicator
                    i18n-label="Unread messages indicator label"
                    label="Unread messages"
                    [class.MessageSpace]="unReadNumber > 99"
                    [counter]="unReadNumber"
                    [size]="UnreadIndicatorSize.SMALL"
                  ></fl-unread-indicator>
                }
              </div>
            </div>
          }
        </div>
      </fl-button>
    </ng-template>

    <ng-container
      *ngTemplateOutlet="button; context: { thread: thread$ | flAsync }"
    ></ng-container>
  `,
  styleUrls: ['./chat-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatButtonComponent implements OnInit, OnChanges {
  private datastore = inject(Datastore);
  private messagingChat = inject(MessagingChat);
  uiConfig = inject<UiConfig>(UI_CONFIG);

  IconColor = IconColor;
  IconSize = IconSize;
  FontWeight = FontWeight;
  UnreadIndicatorPosition = UnreadIndicatorPosition;
  UnreadIndicatorSize = UnreadIndicatorSize;

  /**
   * Other members of the chat.
   * The online indicator will use the first member in the list
   */
  @Input() otherMemberIds: readonly number[];

  /**
   * Override thread type where the default value is
   * PRIVATE if otherMembers length is 1 or
   * GROUP if otherMembers length is greater than 1.
   */
  @Input() threadType?: ThreadType;
  @Input() buttonText?: string = $localize`:Chat button (verb):Chat`;
  @Input() contextType: ThreadContextType;
  @Input() contextId: number;
  @Input() disabled?: boolean = false;
  @Input() color = ButtonColor.DEFAULT;

  @HostBinding('attr.data-display')
  @Input()
  display: ButtonDisplay = 'inline';
  @HostBinding('attr.data-display-tablet')
  @Input()
  displayTablet?: ButtonDisplay;
  @HostBinding('attr.data-display-desktop')
  @Input()
  displayDesktop?: ButtonDisplay;
  /**
   * By default the chat button has a fixed size,
   * setting this to true fits the button to the
   * container width
   */
  @Input()
  fullWidth = false;

  /** Size of the button. Automatically adjusts online indicator size. */
  @Input() size = ButtonSize.SMALL;
  onlineIndicatorSize = OnlineIndicatorSize.SMALL;

  @Input() isIconOnly = false;
  @Input() iconLabel?: string;

  @Input() showOnlineIndicator = true;

  isOnline$: Observable<boolean>;
  thread$: Observable<Thread | undefined>;
  unreadMessagesNumber$: Observable<number | false>;

  ngOnInit(): void {
    this.setupOtherMembers();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('size' in changes) {
      switch (this.size) {
        case ButtonSize.LARGE:
          this.onlineIndicatorSize = OnlineIndicatorSize.LARGE;
          break;
        case ButtonSize.XLARGE:
          this.onlineIndicatorSize = OnlineIndicatorSize.XXLARGE;
          break;
        default:
          this.onlineIndicatorSize = OnlineIndicatorSize.SMALL;
      }
    }

    if (
      ('otherMemberIds' in changes || 'threadType' in changes) &&
      this.otherMemberIds
    ) {
      this.setupOtherMembers();
    }
  }

  chat(threadId: number | undefined): void {
    this.messagingChat.startChat({
      threadId,
      userIds: this.otherMemberIds,
      threadType: this.getThreadType(),
      origin: '', // unused, even by the messaging chat component
      context: this.getThreadContext(),
    });
  }

  private setupOtherMembers(): void {
    if (this.showOnlineIndicator) {
      this.isOnline$ = this.datastore
        .document<OnlineOfflineCollection>(
          'onlineOffline',
          this.otherMemberIds[0],
        )
        .valueChanges()
        .pipe(
          map(status => status.status === OnlineOfflineStatusApi.ONLINE),
          startWith(false),
        );
    }

    this.thread$ = this.datastore
      .collection<ThreadsCollection>('threads', query =>
        this.contextType && this.contextId
          ? query
              .where('otherMembers', 'intersects', this.otherMemberIds)
              .where('context', '==', this.getThreadContext())
              .where('threadType', '==', this.getThreadType())
          : query
              .where('otherMembers', 'intersects', this.otherMemberIds)
              .where('threadType', '==', this.getThreadType()),
      )
      .valueChanges()
      .pipe(
        map(threads =>
          this.messagingChat.filterThreadByOtherMembers(
            threads,
            this.otherMemberIds,
          ),
        ),
      );

    this.unreadMessagesNumber$ = this.thread$.pipe(
      map(thread => !!thread && thread.messageUnreadCount),
    );
  }

  private getThreadType(): ThreadType {
    if (this.threadType) {
      return this.threadType;
    }

    return this.otherMemberIds.length === 1
      ? ThreadTypeApi.PRIVATE_CHAT
      : ThreadTypeApi.GROUP;
  }

  private getThreadContext(): ThreadContext {
    return {
      type: this.contextType,
      id: this.contextId,
    };
  }
}
